import {useCallback, useState} from 'react';
import {trpc, RouterOutput, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {
  DataTable,
  useDataTable,
  type Column,
  type UseDataTableReturn,
} from '@common-web/components/data-table';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {useAuthStore} from '@common-web/stores/authStore';
import {useClientActionAllowed, useHasPermission} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {
  Users,
  Loader2,
  Plus,
  UserCheck,
  Clock,
  Mail,
  ChevronRight,
  Send,
  Archive,
  ArchiveRestore,
  KeyRound,
  Ban,
  Power,
} from 'lucide-react';
import {getUserTypeBadge, getRoleBadge} from './components/userTypeBadges';
import {format, formatDistanceToNow} from 'date-fns';
import {useNavigate} from 'react-router-dom';

type User = RouterOutput['internal']['userManagement']['listUsers']['users'][0];
type PendingRegistration =
  RouterOutput['internal']['userManagement']['getPendingRegistrations']['registrations'][0];
type ExternalUserRow = RouterOutput['internal']['userManagement']['listExternalUsers'][number];

type SearchType = 'ALL' | 'INTERNAL' | 'CLIENT' | 'GROUP' | 'BROKER';

type ListUsersOutput = RouterOutput['internal']['userManagement']['listUsers'];

// TODO(roles): redo roles per https://app.asana.com/1/1206214889537900/project/1212886114840713/task/1214505031370452?focus=true

type TabMode = 'active' | 'archived';

const TAB_CONFIG: Record<TabMode, {countNoun: string; emptyMessage: string; label: string}> = {
  active: {
    countNoun: 'user',
    emptyMessage: 'No users found',
    label: 'Active Users',
  },
  archived: {
    countNoun: 'archived user',
    emptyMessage: 'No archived users',
    label: 'Archived Users',
  },
};

type TabValue = 'users' | 'pending' | 'archived' | 'external';

type RestoreControls = {
  isAnyRestorePending: boolean;
  onRestore: (userId: string) => void;
  restoringUserId: string | null;
};

type UsersTableFilters = {type: string};

type UsersTabContentProps = {
  errorMessage: string | undefined;
  isInternalUser: boolean;
  isLoading: boolean;
  mode: TabMode;
  restoreControls?: RestoreControls;
  table: UseDataTableReturn<UsersTableFilters>;
  usersData: ListUsersOutput | undefined;
};

function UsersTabContent({
  errorMessage,
  isInternalUser,
  isLoading,
  mode,
  restoreControls,
  table,
  usersData,
}: UsersTabContentProps) {
  const navigate = useNavigate();
  const {countNoun, emptyMessage, label} = TAB_CONFIG[mode];

  const userColumns: Column<User>[] = [
    {
      id: 'user',
      header: 'User',
      render: (user) => (
        <div>
          <div className='font-medium text-brand-navy'>{user.name}</div>
          <div className='text-sm text-brand-gray flex items-center gap-1'>
            <Mail className='h-3 w-3' />
            {user.email}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type & Role',
      render: (user) => (
        <div className='flex flex-col items-start gap-2'>
          {getUserTypeBadge(user.type)}
          {user.role && getRoleBadge(user.role)}
        </div>
      ),
    },
    {
      id: 'access',
      header: 'Access',
      render: (user) => (
        <div className='space-y-1'>
          {user.clientProfile && (
            <div className='text-sm'>
              <span className='text-brand-gray'>Client:</span>{' '}
              <span className='font-medium text-brand-navy'>{user.clientProfile.client.name}</span>
            </div>
          )}
          {user.groupProfiles.length > 0 && (
            <div className='text-sm'>
              <span className='text-brand-gray'>Groups:</span>{' '}
              <span className='font-medium text-brand-navy'>
                {user.groupProfiles.length} group{user.groupProfiles.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {user.brokerProfile && (
            <div className='text-sm'>
              <span className='text-brand-gray'>Agent:</span>{' '}
              <span className='font-medium text-brand-navy'>{user.brokerProfile.agent.name}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'activity',
      header: 'Activity',
      render: (user) => (
        <div className='text-sm space-y-1'>
          <div className='text-brand-gray'>
            Created: {format(new Date(user.createdAt), 'MMM d, yyyy')}
          </div>
          {user.lastLogin ? (
            <div className='text-brand-gray'>
              Last login: {formatDistanceToNow(new Date(user.lastLogin), {addSuffix: true})}
            </div>
          ) : (
            <div className='text-brand-gray/50 italic'>Never logged in</div>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (user) => {
        const showRestore = !!(restoreControls && user.archivedAt);
        const isRestoring = restoreControls?.restoringUserId === user.id;
        return (
          <div className='flex justify-end gap-2'>
            {showRestore && (
              <Button
                size='sm'
                variant='outline'
                className='border-emerald/30 text-emerald hover:bg-emerald/10'
                onClick={() => restoreControls!.onRestore(user.id)}
                disabled={restoreControls!.isAnyRestorePending}
              >
                {isRestoring ? (
                  <Loader2 className='h-3 w-3 animate-spin mr-1' />
                ) : (
                  <ArchiveRestore className='h-3 w-3 mr-1' />
                )}
                Restore
              </Button>
            )}
            <Button
              size='sm'
              className='bg-brand-blue hover:bg-brand-blue-dark text-white font-medium'
              onClick={() => navigate(`/users/${user.id}`)}
            >
              View
              <ChevronRight className='h-4 w-4 ml-1' />
            </Button>
          </div>
        );
      },
    },
  ];

  const typeOptions = [
    ...(isInternalUser ? [{value: 'INTERNAL', label: 'Internal Users'}] : []),
    {value: 'CLIENT', label: 'Client Admins'},
    {value: 'GROUP', label: 'Group Users'},
    {value: 'BROKER', label: 'Brokers'},
  ];

  return (
    <DataTable<User, UsersTableFilters>
      table={table}
      columns={userColumns}
      data={usersData?.users}
      total={usersData?.total}
      totalPages={usersData?.totalPages}
      loading={isLoading}
      error={errorMessage ? new Error(errorMessage) : null}
      emptyMessage={emptyMessage}
      title={label}
      entity={{singular: countNoun, plural: `${countNoun}s`}}
      searchPlaceholder='Search by name or email...'
      filters={[
        {
          key: 'type',
          label: 'Users',
          type: 'select',
          placeholder: 'Filter by type',
          options: typeOptions,
        },
      ]}
      rowKey={(user) => user.id}
    />
  );
}

export function InternalUsers() {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('users');
  // These mirror the server-side gates in user-management-router: they only
  // constrain client-profile viewers — internal users always pass.
  const canInviteUser = useClientActionAllowed(CODE_PERMISSIONS.userManagement.inviteUser.key);
  const canArchiveUser = useClientActionAllowed(CODE_PERMISSIONS.userManagement.archiveUser.key);
  const canResendRegistration = useClientActionAllowed(
    CODE_PERMISSIONS.userManagement.generateRegistrationCodes.key,
  );
  const canRevokeRegistration = useClientActionAllowed(
    CODE_PERMISSIONS.userManagement.revokeRegistrationCodes.key,
  );
  // Strict permission gate (not useClientActionAllowed): internal users
  // without the key must not see the External API tab either.
  const canManageExternalUsers = useHasPermission(
    CODE_PERMISSIONS.userManagement.manageExternalUsers.key,
  );

  const usersTable = useDataTable<UsersTableFilters>({
    defaultPageSize: 20,
    defaultFilters: {type: ''},
    searchDebounce: 900,
    syncToUrl: 'users',
  });

  const pendingTable = useDataTable({defaultPageSize: 20, syncToUrl: 'pending'});

  const {
    data: usersData,
    isLoading,
    isFetching,
    error,
    refetch: refetchUsers,
  } = trpc.internal.userManagement.listUsers.useQuery(
    {
      archived: activeTab === 'archived',
      page: usersTable.page,
      pageSize: usersTable.pageSize,
      search: usersTable.debouncedSearch,
      type: (usersTable.filterValues.type || 'ALL') as SearchType,
    },
    {enabled: activeTab !== 'pending'},
  );

  const {
    data: registrationsData,
    isLoading: regLoading,
    refetch: refetchRegistrations,
  } = trpc.internal.userManagement.getPendingRegistrations.useQuery({
    page: pendingTable.page,
    pageSize: pendingTable.pageSize,
  });

  const cancelRegistration = trpc.internal.userManagement.cancelRegistration.useMutation({
    onSuccess: () => {
      refetchRegistrations();
    },
    onError: createMutationErrorHandler('Registration cancellation failed'),
  });

  const [resendingRegistrationId, setResendingRegistrationId] = useState<string | null>(null);

  const resendRegistrationEmail = trpc.internal.userManagement.resendRegistrationEmail.useMutation({
    onSuccess: () => {
      refetchRegistrations();
    },
    onSettled: () => {
      setResendingRegistrationId(null);
    },
    onError: createMutationErrorHandler('Failed to resend registration email'),
  });

  const [restoringUserId, setRestoringUserId] = useState<string | null>(null);

  const unarchiveUser = trpc.internal.userManagement.unarchiveUser.useMutation({
    onSuccess: () => {
      refetchUsers();
    },
    onSettled: () => {
      setRestoringUserId(null);
    },
    onError: createMutationErrorHandler('Failed to unarchive user'),
  });

  const handleRestore = useCallback(
    (userId: string) => {
      setRestoringUserId(userId);
      unarchiveUser.mutate({id: userId});
    },
    [unarchiveUser.mutate],
  );

  const {
    data: externalUsers,
    isLoading: externalLoading,
    refetch: refetchExternalUsers,
  } = trpc.internal.userManagement.listExternalUsers.useQuery(undefined, {
    enabled: isInternalUser && canManageExternalUsers && activeTab === 'external',
  });

  const [togglingExternalUserId, setTogglingExternalUserId] = useState<string | null>(null);

  const setExternalUserActive = trpc.internal.userManagement.setExternalUserActive.useMutation({
    onSuccess: () => {
      refetchExternalUsers();
    },
    onSettled: () => {
      setTogglingExternalUserId(null);
    },
    onError: createMutationErrorHandler('Failed to update external user'),
  });

  const handleToggleExternalUser = useCallback(
    (id: string, isActive: boolean) => {
      setTogglingExternalUserId(id);
      setExternalUserActive.mutate({id, isActive});
    },
    [setExternalUserActive.mutate],
  );

  const registrationColumns: Column<PendingRegistration>[] = [
    {
      id: 'user',
      header: 'Pending User',
      render: (reg) => (
        <div>
          <div className='font-medium text-brand-navy'>{reg.name}</div>
          <div className='text-sm text-brand-gray flex items-center gap-1'>
            <Mail className='h-3 w-3' />
            {reg.email}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      render: (reg) => getUserTypeBadge(reg.type),
    },
    {
      id: 'code',
      header: 'Access Code',
      render: (reg) => (
        <div className='font-mono text-sm bg-gray-100 px-2 py-1 rounded inline-block'>
          {reg.code}
        </div>
      ),
    },
    {
      id: 'expires',
      header: 'Expires',
      render: (reg) => (
        <div className='text-sm flex items-center gap-1 text-brand-gray'>
          <Clock className='h-3 w-3' />
          {format(new Date(reg.expiresAt), 'MMM d, yyyy h:mm a')}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (reg) => (
        <div className='flex gap-2'>
          {canResendRegistration && (
            <Button
              size='sm'
              variant='outline'
              className='border-brand-blue/20 text-brand-blue hover:bg-brand-blue/5'
              onClick={() => {
                setResendingRegistrationId(reg.id);
                resendRegistrationEmail.mutate({id: reg.id});
              }}
              disabled={resendRegistrationEmail.isPending}
            >
              {resendingRegistrationId === reg.id && resendRegistrationEmail.isPending ? (
                <Loader2 className='h-3 w-3 animate-spin mr-1' />
              ) : (
                <Send className='h-3 w-3 mr-1' />
              )}
              Resend
            </Button>
          )}
          {canRevokeRegistration && (
            <Button
              size='sm'
              variant='outline'
              className='border-red-200 text-red-600 hover:bg-red-50'
              onClick={() => cancelRegistration.mutate({id: reg.id})}
              disabled={cancelRegistration.isPending}
            >
              Cancel
            </Button>
          )}
        </div>
      ),
    },
  ];

  const externalColumns: Column<ExternalUserRow>[] = [
    {
      id: 'name',
      header: 'Integration',
      render: (u) => (
        <div>
          <div className='font-medium text-brand-navy'>{u.name}</div>
          <div className='text-sm text-brand-gray'>{u.clientName}</div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      render: (u) => (
        <Badge className='bg-brand-blue/10 text-brand-blue border-0 font-medium'>{u.type}</Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (u) =>
        u.isActive ? (
          <Badge className='bg-emerald/10 text-emerald border-0 font-medium'>Active</Badge>
        ) : (
          <Badge className='bg-red-100 text-red-700 border-0 font-medium'>Revoked</Badge>
        ),
    },
    {
      id: 'activity',
      header: 'Activity',
      render: (u) => (
        <div className='text-sm space-y-1'>
          <div className='text-brand-gray'>
            Created: {format(new Date(u.createdAt), 'MMM d, yyyy')}
          </div>
          <div className='text-brand-gray'>
            {u.apiCallCount} call{u.apiCallCount === 1 ? '' : 's'}
          </div>
          {u.lastUsedAt ? (
            <div className='text-brand-gray'>
              Last used: {formatDistanceToNow(new Date(u.lastUsedAt), {addSuffix: true})}
            </div>
          ) : (
            <div className='text-brand-gray/50 italic'>Never used</div>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (u) => {
        const isToggling = togglingExternalUserId === u.id;
        return (
          <div className='flex justify-end'>
            <Button
              size='sm'
              variant='outline'
              className={
                u.isActive
                  ? 'border-red-200 text-red-600 hover:bg-red-50'
                  : 'border-emerald/30 text-emerald hover:bg-emerald/10'
              }
              onClick={() => handleToggleExternalUser(u.id, !u.isActive)}
              disabled={setExternalUserActive.isPending}
            >
              {isToggling ? (
                <Loader2 className='h-3 w-3 animate-spin mr-1' />
              ) : u.isActive ? (
                <Ban className='h-3 w-3 mr-1' />
              ) : (
                <Power className='h-3 w-3 mr-1' />
              )}
              {u.isActive ? 'Revoke' : 'Reactivate'}
            </Button>
          </div>
        );
      },
    },
  ];

  const sharedTabProps: Omit<UsersTabContentProps, 'mode' | 'restoreControls'> = {
    errorMessage: error?.message,
    isInternalUser,
    isLoading,
    table: usersTable,
    usersData,
  };

  const archivedRestoreControls: RestoreControls = {
    isAnyRestorePending: unarchiveUser.isPending,
    onRestore: handleRestore,
    restoringUserId,
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='User Management'
          description='Manage users, registration codes, and access permissions'
          icon={<Users className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          {canInviteUser && (
            <PageHeaderAction onClick={() => navigate('/users/new')}>
              <Plus className='h-4 w-4 mr-2' />
              Create User
            </PageHeaderAction>
          )}
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <Tabs value={activeTab} onValueChange={(next) => setActiveTab(next as TabValue)}>
            <TabsList className='bg-gray-100 p-1 mb-6'>
              <TabsTrigger
                value='users'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <UserCheck className='h-4 w-4 mr-2' />
                {TAB_CONFIG.active.label}
              </TabsTrigger>
              <TabsTrigger
                value='pending'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <Clock className='h-4 w-4 mr-2' />
                Pending Registrations
              </TabsTrigger>
              <TabsTrigger
                value='archived'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <Archive className='h-4 w-4 mr-2' />
                {TAB_CONFIG.archived.label}
              </TabsTrigger>
              {isInternalUser && canManageExternalUsers && (
                <TabsTrigger
                  value='external'
                  className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                >
                  <KeyRound className='h-4 w-4 mr-2' />
                  External API
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value='users' className='mt-0'>
              <UsersTabContent mode='active' {...sharedTabProps} />
            </TabsContent>

            <TabsContent value='archived' className='mt-0'>
              <UsersTabContent
                mode='archived'
                restoreControls={canArchiveUser ? archivedRestoreControls : undefined}
                {...sharedTabProps}
              />
            </TabsContent>

            {isInternalUser && canManageExternalUsers && (
              <TabsContent value='external' className='mt-0'>
                <Card className='border-gray-200 shadow-sm'>
                  <CardHeader className='bg-gray-50 border-b py-3'>
                    <div className='flex items-center justify-between'>
                      <CardTitle className='text-lg font-semibold text-brand-navy'>
                        External API Integrations
                      </CardTitle>
                      {externalUsers && (
                        <Badge className='bg-brand-blue/10 text-brand-blue border-0 font-medium px-3 py-1'>
                          {externalUsers.length} integration
                          {externalUsers.length === 1 ? '' : 's'}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className='p-0'>
                    <DataTable<ExternalUserRow>
                      columns={externalColumns}
                      data={externalUsers}
                      loading={externalLoading}
                      emptyMessage='No external API integrations'
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            <TabsContent value='pending' className='mt-0'>
              <DataTable<PendingRegistration>
                table={pendingTable}
                columns={registrationColumns}
                data={registrationsData?.registrations}
                total={registrationsData?.total}
                totalPages={registrationsData?.totalPages}
                loading={regLoading}
                emptyMessage='No pending registrations'
                title='Pending Registrations'
                entity={{singular: 'pending registration', plural: 'pending registrations'}}
                rowKey={(reg) => reg.id}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
